import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Brain, ChartLine, Users, ArrowRight, Barbell, ForkKnife, MoonStars,
  ClipboardText, Gauge, ChatsCircle, TrendUp, ClipboardText as Assess,
  Check, Sparkle, PaperPlaneTilt, CheckCircle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=2000&q=80";

const features = [
  { icon: Barbell, title: "Workout Tracker", desc: "Log sets, reps, and durations. Built-in timer for rest periods." },
  { icon: Brain, title: "AI Program Designer", desc: "AI-assisted programs tailored to your goals and equipment." },
  { icon: ForkKnife, title: "Nutrition Logging", desc: "Track calories, macros, and meals with precision." },
  { icon: MoonStars, title: "Recovery & Sleep", desc: "Monitor sleep, soreness, stress — get a daily readiness score." },
  { icon: ChartLine, title: "Goals & Progress", desc: "Set targets. Visualize trends. Hit milestones." },
  { icon: Users, title: "Coach & Athlete", desc: "Assign programs, review readiness, share feedback in one place." },
];

// Genuine platform benefits (no fabricated statistics)
const benefits = [
  { icon: Assess, label: "Professional assessments" },
  { icon: ClipboardText, label: "Structured programming" },
  { icon: Gauge, label: "Readiness monitoring" },
  { icon: ChatsCircle, label: "Coach–athlete feedback" },
  { icon: TrendUp, label: "Progress tracking" },
];

const steps = [
  {
    n: "01",
    icon: Assess,
    title: "Complete Your Assessment",
    desc: "Record fitness, mobility, strength and sport-specific results.",
  },
  {
    n: "02",
    icon: Brain,
    title: "Receive Your Training Program",
    desc: "Get a structured program created by your coach or with AI assistance.",
  },
  {
    n: "03",
    icon: Gauge,
    title: "Log Training and Readiness",
    desc: "Record workouts, loads, RPE/RIR, sleep, soreness, energy and pain.",
  },
  {
    n: "04",
    icon: TrendUp,
    title: "Review and Improve",
    desc: "Track progress, receive coach feedback and update the program.",
  },
];

// Realistic sample assessment (mirrors real categories: body comp, mobility, strength, speed/power, recovery)
const assessmentSample = [
  { group: "Body Composition", rows: [["Body weight", "78.4", "kg"], ["Body fat", "14.2", "%"]] },
  { group: "Mobility", rows: [["Ankle dorsiflexion", "11", "cm"], ["Overhead reach", "Pass", ""]] },
  { group: "Strength", rows: [["Back Squat 1RM", "140", "kg"], ["Bench Press 1RM", "95", "kg"]] },
  { group: "Speed & Power", rows: [["10m Sprint", "1.78", "s"], ["Vertical Jump", "58", "cm"]] },
  { group: "Recovery", rows: [["Resting HRV", "68", "ms"], ["Sleep", "7.5", "hrs"]] },
];

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const priceNode = (pkg, unit) =>
  pkg ? (
    <span className="font-heading text-5xl tabular-nums">
      {inr(pkg.amount_inr)}
      <span className="text-text-secondary text-base font-body">/{unit}</span>
    </span>
  ) : (
    <span className="font-heading text-2xl text-white/70">Pricing coming soon</span>
  );

export default function Landing() {
  const [packages, setPackages] = useState(null); // null = loading, [] = unavailable
  const [pricingTab, setPricingTab] = useState("athlete"); // 'athlete' | 'coach'
  const [athleteBilling, setAthleteBilling] = useState("monthly"); // 'monthly' | 'yearly'
  const [demoForm, setDemoForm] = useState({ name: "", email: "", organization: "", team_size: "", message: "" });
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoSent, setDemoSent] = useState(false);

  const updateDemo = (field) => (e) => setDemoForm((f) => ({ ...f, [field]: e.target.value }));

  const submitDemo = async (e) => {
    e.preventDefault();
    if (!demoForm.name.trim() || !demoForm.email.trim()) {
      toast.error("Please add your name and email");
      return;
    }
    setDemoSubmitting(true);
    try {
      await api.post("/demo-requests", demoForm);
      setDemoSent(true);
      toast.success("Thanks — we'll be in touch shortly");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send — please try again");
    } finally {
      setDemoSubmitting(false);
    }
  };

  useEffect(() => {
    let alive = true;
    api.get("/pro/packages")
      .then(({ data }) => { if (alive) setPackages(Array.isArray(data) ? data : []); })
      .catch(() => { if (alive) setPackages([]); });
    return () => { alive = false; };
  }, []);

  const byId = (id) => (packages || []).find((p) => p.id === id);
  const proMonthly = byId("pro_monthly");
  const proYearly = byId("pro_yearly");
  const coachPro = byId("coach_pro_monthly");
  const coachProYearly = byId("coach_pro_yearly");
  const coachPlus = byId("coach_plus_monthly");
  const pricingReady = packages && packages.length > 0;

  const savePct = (monthlyPkg, yearlyPkg) => {
    if (!monthlyPkg || !yearlyPkg) return null;
    const full = monthlyPkg.amount_inr * 12;
    if (!full || yearlyPkg.amount_inr >= full) return null;
    return Math.round((1 - yearlyPkg.amount_inr / full) * 100);
  };
  const athleteSave = savePct(proMonthly, proYearly);
  const coachSave = savePct(coachPro, coachProYearly);

  const isYearlyBilling = athleteBilling === "yearly";
  const proSelected = isYearlyBilling ? proYearly : proMonthly;

  const athleteTiers = [
    {
      key: "free",
      name: "Free",
      priceNode: <span className="font-heading text-5xl tabular-nums">₹0</span>,
      sub: "Get started, no card required",
      perks: ["Workout, nutrition & recovery tracking", "Readiness score & goals", "3 AI programs / month"],
      cta: "START FREE",
      highlight: false,
    },
    {
      key: "athlete-pro",
      name: "Athlete Pro",
      priceNode: priceNode(proSelected, isYearlyBilling ? "yr" : "mo"),
      sub: proSelected
        ? (isYearlyBilling ? "12 months, billed annually" : "Billed monthly · cancel anytime")
        : "Contact us for details",
      saveBadge: isYearlyBilling && athleteSave ? `SAVE ${athleteSave}%` : null,
      billingToggle: true,
      perks: ["Unlimited AI programs", "AI Form Check & advanced analytics", "Full PB history & PDF reports"],
      cta: isYearlyBilling ? "GO PRO YEARLY" : "GO PRO",
      highlight: true,
    },
  ];

  const coachTiers = [
    {
      key: "coach-pro-monthly",
      name: "Coach Pro",
      priceNode: priceNode(coachPro, "mo"),
      sub: coachPro ? "Up to 10 active clients" : "Contact us for details",
      perks: ["Manage clients with full visibility", "Program builder & exercise library", "Readiness, pain & adherence alerts"],
      cta: "START COACHING",
      highlight: false,
    },
    {
      key: "coach-plus-monthly",
      name: "Coach Plus",
      priceNode: priceNode(coachPlus, "mo"),
      sub: coachPlus ? "Up to 40 active clients" : "Contact us for details",
      perks: ["Everything in Coach Pro", "Manage up to 40 clients", "Priority AI & academy tools"],
      cta: "SCALE UP",
      highlight: true,
    },
    {
      key: "coach-pro-yearly",
      name: "Coach Pro — Yearly",
      priceNode: priceNode(coachProYearly, "yr"),
      sub: coachProYearly ? "Up to 10 clients · billed annually" : "Contact us for details",
      saveBadge: coachSave ? `SAVE ${coachSave}%` : null,
      perks: ["Everything in Coach Pro", "12 months billed annually", "Lock in your rate"],
      cta: "GO YEARLY",
      highlight: false,
    },
  ];

  const pricingTiers = pricingTab === "coach" ? coachTiers : athleteTiers;

  return (
    <div className="min-h-screen landing-dark bg-surface-base text-white" data-testid="landing-page">
      {/* Nav */}
      <nav className="crystal-glass fixed top-0 inset-x-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="brand-logo">
            <img src="/edge-logo.png" alt="Edge Training" className="h-11 w-11 rounded-lg shadow-md" />
            <span className="font-heading text-2xl tracking-tight text-white leading-none">EDGE TRAINING</span>
          </div>
          <div className="hidden md:flex items-center gap-8 label-tiny">
            <a href="#features" className="hover:text-heat text-white/80 transition-colors" data-testid="nav-features-link">Features</a>
            <a href="#how" className="hover:text-heat text-white/80 transition-colors" data-testid="nav-how-link">How it Works</a>
            <a href="#pricing" className="hover:text-heat text-white/80 transition-colors" data-testid="nav-pricing-link">Pricing</a>
            <a href="#demo" className="hover:text-heat text-white/80 transition-colors" data-testid="nav-demo-link">Book a Demo</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-link">
              <Button variant="ghost" className="text-white hover:bg-white/5 label-tiny">LOG IN</Button>
            </Link>
            <Link to="/register" data-testid="nav-signup-link">
              <button className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest" data-testid="nav-cta-button">
                START FREE
              </button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-24 min-h-screen overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-28">
          <div className="max-w-4xl animate-fade-up">
            <div className="flex items-center gap-2 mb-6">
              <span className="w-2 h-2 rounded-full bg-heat pulse-dot" />
              <span className="label-tiny text-heat">THE COACHING OPERATING SYSTEM</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[0.98] mb-6" data-testid="hero-heading">
              THE COACHING OPERATING SYSTEM FOR{" "}
              <span className="text-heat neon-yellow">ATHLETES, COACHES</span> AND{" "}
              <span className="text-heat">ACADEMIES.</span>
            </h1>
            <p className="text-white/85 text-base sm:text-lg max-w-2xl mb-8" data-testid="hero-subtext">
              Assess performance, build structured programs, monitor readiness and track
              measurable progress — all in one platform.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/register" data-testid="hero-cta-button">
                <button className="btn-kinetic px-8 py-4 text-sm label-tiny tracking-widest flex items-center gap-2">
                  START TRAINING FREE
                  <ArrowRight size={16} weight="bold" />
                </button>
              </Link>
              <Link to="/login" data-testid="hero-login-button">
                <button className="border border-white/50 text-white hover:bg-white hover:text-surface-base px-8 py-4 text-sm label-tiny tracking-widest transition-colors">
                  SIGN IN
                </button>
              </Link>
            </div>
          </div>

          {/* Genuine platform benefits (replaces fabricated stat bar) */}
          <div className="mt-16 flex flex-wrap gap-3 max-w-4xl" data-testid="benefit-chips">
            {benefits.map((b) => (
              <div
                key={b.label}
                className="flex items-center gap-2.5 bg-white/[0.04] border border-white/10 px-4 py-3 backdrop-blur-sm"
                data-testid={`benefit-chip-${b.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <b.icon size={20} weight="duotone" className="text-heat flex-shrink-0" />
                <span className="text-sm text-white/90">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative py-24 px-6 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 max-w-2xl">
            <div className="label-tiny text-heat mb-4">/ ARSENAL</div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-4">
              EVERY TOOL YOU<br />NEED TO PERFORM.
            </h2>
            <p className="text-white/80">One connected system for assessments, programming, readiness and progress.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {features.map((f, i) => (
              <div key={f.title} className="bg-surface-base p-8 hover:bg-surface-DEFAULT transition-colors group" data-testid={`feature-card-${i}`}>
                <f.icon size={32} weight="duotone" className="text-brand-accent mb-6 group-hover:scale-110 transition-transform" />
                <h3 className="font-heading text-xl mb-3">{f.title}</h3>
                <p className="text-sm text-white/80">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how" className="relative py-24 px-6 border-t border-white/10 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 max-w-2xl">
            <div className="label-tiny text-heat mb-4">/ HOW IT WORKS</div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-4">
              FROM ASSESSMENT<br />TO MEASURABLE PROGRESS.
            </h2>
            <p className="text-white/80">A simple, repeatable loop that keeps athletes and coaches aligned.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10" data-testid="how-it-works-steps">
            {steps.map((s, i) => (
              <div
                key={s.n}
                className="bg-surface-base p-8 hover:bg-surface-DEFAULT transition-colors group relative"
                data-testid={`how-step-${i}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="font-heading text-4xl text-heat/30 tabular-nums group-hover:text-heat/60 transition-colors">{s.n}</span>
                  <div className="w-11 h-11 border border-brand-accent flex items-center justify-center">
                    <s.icon size={22} weight="duotone" className="text-brand-accent" />
                  </div>
                </div>
                <h3 className="font-heading text-lg mb-3 leading-tight">{s.title}</h3>
                <p className="text-sm text-white/80">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* Assessment sample preview — shows athletes what step 01 looks like */}
          <div className="mt-12 grid lg:grid-cols-5 gap-px bg-white/10" data-testid="assessment-preview">
            <div className="bg-surface-base p-8 lg:col-span-2 flex flex-col justify-center">
              <div className="label-tiny text-heat mb-3">/ STEP 01 · PREVIEW</div>
              <h3 className="font-heading text-2xl mb-3 leading-tight">SEE WHAT YOU'LL FILL IN.</h3>
              <p className="text-sm text-white/80">
                Your first assessment captures body composition, mobility, strength, speed &amp;
                power and recovery — the baseline your program and progress are built on. Coaches
                can add sport-specific tests too.
              </p>
            </div>
            <div className="bg-surface-base p-6 sm:p-8 lg:col-span-3">
              <div className="flex items-center justify-between mb-5">
                <span className="font-heading text-base">BASELINE ASSESSMENT</span>
                <span className="label-tiny text-white/50">SAMPLE</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
                {assessmentSample.map((cat) => (
                  <div key={cat.group} data-testid={`assessment-group-${cat.group.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <div className="label-tiny text-brand-accent mb-3">{cat.group}</div>
                    <div className="space-y-2.5">
                      {cat.rows.map(([label, value, unit]) => (
                        <div key={label} className="flex items-center justify-between gap-3">
                          <span className="text-sm text-white/75">{label}</span>
                          <span className="flex items-baseline gap-1 border-b border-white/15 px-2 pb-1 min-w-[84px] justify-end">
                            <span className="font-heading text-base tabular-nums text-white">{value}</span>
                            {unit && <span className="text-xs text-white/50">{unit}</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative py-24 px-6 border-t border-white/10 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10 max-w-2xl">
            <div className="label-tiny text-heat mb-4">/ PRICING</div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-4">
              START FREE.<br /><span className="text-heat">SCALE WHEN READY.</span>
            </h2>
            <p className="text-white/80">
              Train free forever. Upgrade for unlimited AI, deeper analytics and coaching tools.
            </p>
          </div>

          {/* Audience toggle */}
          <div className="flex gap-3 mb-10" data-testid="pricing-audience-toggle" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={pricingTab === "athlete"}
              onClick={() => setPricingTab("athlete")}
              data-testid="pricing-tab-athlete"
              className={`px-5 py-2.5 text-xs label-tiny tracking-widest border transition-colors ${
                pricingTab === "athlete"
                  ? "bg-brand-accent text-white border-brand-accent"
                  : "border-white/20 text-white/70 hover:border-brand-accent hover:text-white"
              }`}
            >
              FOR ATHLETES
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={pricingTab === "coach"}
              onClick={() => setPricingTab("coach")}
              data-testid="pricing-tab-coach"
              className={`px-5 py-2.5 text-xs label-tiny tracking-widest border transition-colors ${
                pricingTab === "coach"
                  ? "bg-brand-accent text-white border-brand-accent"
                  : "border-white/20 text-white/70 hover:border-brand-accent hover:text-white"
              }`}
            >
              FOR COACHES & ACADEMIES
            </button>
          </div>

          {packages === null ? (
            <div className="text-white/60 label-tiny" data-testid="pricing-loading">Loading plans…</div>
          ) : (
            <div
              className={`grid gap-px bg-white/10 ${pricingTab === "coach" ? "md:grid-cols-3" : "md:grid-cols-2 max-w-3xl"}`}
              data-testid="pricing-grid"
            >
              {pricingTiers.map((t) => (
                <div
                  key={t.key}
                  className={`bg-surface-base p-8 flex flex-col relative ${t.highlight ? "ring-1 ring-heat/50" : ""}`}
                  data-testid={`pricing-card-${t.key}`}
                >
                  {t.saveBadge && (
                    <span
                      className="absolute top-4 right-4 bg-heat text-[#0A0A0F] font-heading text-[11px] tracking-widest uppercase px-2.5 py-1 leading-none"
                      data-testid={`pricing-save-badge-${t.key}`}
                    >
                      {t.saveBadge}
                    </span>
                  )}
                  {t.highlight && (
                    <div className="label-tiny text-heat mb-3 flex items-center gap-1">
                      <Sparkle size={12} weight="fill" /> MOST POPULAR
                    </div>
                  )}
                  <h3 className="font-heading text-xl mb-4">{t.name.toUpperCase()}</h3>
                  {t.billingToggle && (
                    <div
                      className="inline-flex w-fit border border-white/15 p-0.5 mb-4"
                      data-testid="athlete-billing-toggle"
                      role="tablist"
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={!isYearlyBilling}
                        onClick={() => setAthleteBilling("monthly")}
                        data-testid="billing-monthly"
                        className={`px-3.5 py-1.5 text-[11px] label-tiny tracking-widest transition-colors ${
                          !isYearlyBilling ? "bg-brand-accent text-white" : "text-white/60 hover:text-white"
                        }`}
                      >
                        MONTHLY
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={isYearlyBilling}
                        onClick={() => setAthleteBilling("yearly")}
                        data-testid="billing-yearly"
                        className={`px-3.5 py-1.5 text-[11px] label-tiny tracking-widest transition-colors flex items-center gap-1.5 ${
                          isYearlyBilling ? "bg-brand-accent text-white" : "text-white/60 hover:text-white"
                        }`}
                      >
                        YEARLY
                        {athleteSave ? <span className="text-heat">−{athleteSave}%</span> : null}
                      </button>
                    </div>
                  )}
                  <div className="mb-1">{t.priceNode}</div>
                  <div className="label-tiny text-white/60 mb-6">{t.sub}</div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {t.perks.map((perk) => (
                      <li key={perk} className="flex items-start gap-2.5 text-sm text-white/85">
                        <Check size={16} weight="bold" className="text-heat flex-shrink-0 mt-0.5" />
                        <span>{perk}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/register" data-testid={`pricing-cta-${t.key}`}>
                    <button
                      className={`w-full py-3 text-xs label-tiny tracking-widest transition-colors ${
                        t.highlight
                          ? "btn-kinetic"
                          : "border border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-white"
                      }`}
                    >
                      {t.cta}
                    </button>
                  </Link>
                </div>
              ))}
            </div>
          )}

          {!pricingReady && packages !== null && (
            <p className="mt-6 text-sm text-white/60" data-testid="pricing-fallback-note">
              Detailed plan pricing is coming soon — <Link to="/register" className="text-heat underline">create a free account</Link> to get started.
            </p>
          )}

          <p className="mt-6 text-sm text-white/60" data-testid="pricing-academy-note">
            Running an academy or team?{" "}
            <a href="#demo" className="text-heat underline">Book a demo</a> and we'll tailor a rollout for you.
          </p>
        </div>
      </section>

      {/* Book a demo / Contact */}
      <section id="demo" className="relative py-24 px-6 border-t border-white/10 scroll-mt-20">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-px bg-white/10">
          <div className="bg-surface-base p-8 sm:p-12 flex flex-col justify-center">
            <div className="label-tiny text-heat mb-4">/ FOR ACADEMIES & TEAMS</div>
            <h2 className="font-heading text-4xl sm:text-5xl mb-4 leading-[0.98]">
              NOT READY TO<br /><span className="text-heat">SELF-SERVE?</span>
            </h2>
            <p className="text-white/80 mb-6 max-w-md">
              Book a walkthrough for your academy or team. We'll show you athlete onboarding,
              coach workflows, assessments and reporting — then help you roll it out.
            </p>
            <ul className="space-y-2.5">
              {["Multi-coach & multi-athlete setup", "Bulk athlete onboarding", "Custom assessment templates", "Rollout & migration support"].map((li) => (
                <li key={li} className="flex items-center gap-2.5 text-sm text-white/85">
                  <Check size={16} weight="bold" className="text-heat flex-shrink-0" />
                  <span>{li}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-surface-base p-8 sm:p-12">
            {demoSent ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-8" data-testid="demo-success">
                <CheckCircle size={56} weight="fill" className="text-heat mb-5" />
                <h3 className="font-heading text-2xl mb-2">REQUEST RECEIVED</h3>
                <p className="text-sm text-white/80 max-w-xs">
                  Thanks, {demoForm.name.split(" ")[0] || "there"}. Our team will reach out at{" "}
                  <span className="text-white">{demoForm.email}</span> shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={submitDemo} className="space-y-4" data-testid="demo-form">
                <div className="label-tiny text-heat mb-2">// BOOK A DEMO</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label-tiny text-white/60 block mb-1.5">YOUR NAME *</label>
                    <input
                      type="text" required value={demoForm.name} onChange={updateDemo("name")}
                      data-testid="demo-name-input"
                      className="w-full bg-white/[0.04] border border-white/15 px-3 py-2.5 text-sm text-white placeholder-white/30 focus:border-brand-accent outline-none"
                      placeholder="Alex Coach"
                    />
                  </div>
                  <div>
                    <label className="label-tiny text-white/60 block mb-1.5">WORK EMAIL *</label>
                    <input
                      type="email" required value={demoForm.email} onChange={updateDemo("email")}
                      data-testid="demo-email-input"
                      className="w-full bg-white/[0.04] border border-white/15 px-3 py-2.5 text-sm text-white placeholder-white/30 focus:border-brand-accent outline-none"
                      placeholder="you@academy.com"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label-tiny text-white/60 block mb-1.5">ORGANISATION</label>
                    <input
                      type="text" value={demoForm.organization} onChange={updateDemo("organization")}
                      data-testid="demo-org-input"
                      className="w-full bg-white/[0.04] border border-white/15 px-3 py-2.5 text-sm text-white placeholder-white/30 focus:border-brand-accent outline-none"
                      placeholder="Elite Sports Academy"
                    />
                  </div>
                  <div>
                    <label className="label-tiny text-white/60 block mb-1.5">TEAM SIZE</label>
                    <select
                      value={demoForm.team_size} onChange={updateDemo("team_size")}
                      data-testid="demo-teamsize-select"
                      className="w-full bg-white/[0.04] border border-white/15 px-3 py-2.5 text-sm text-white focus:border-brand-accent outline-none"
                    >
                      <option value="" className="bg-surface-base">Select…</option>
                      <option value="1-10" className="bg-surface-base">1–10 athletes</option>
                      <option value="11-25" className="bg-surface-base">11–25 athletes</option>
                      <option value="26-50" className="bg-surface-base">26–50 athletes</option>
                      <option value="50+" className="bg-surface-base">50+ athletes</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="label-tiny text-white/60 block mb-1.5">WHAT DO YOU NEED?</label>
                  <textarea
                    rows={3} value={demoForm.message} onChange={updateDemo("message")}
                    data-testid="demo-message-input"
                    className="w-full bg-white/[0.04] border border-white/15 px-3 py-2.5 text-sm text-white placeholder-white/30 focus:border-brand-accent outline-none resize-none"
                    placeholder="Tell us about your academy and goals…"
                  />
                </div>
                <button
                  type="submit" disabled={demoSubmitting}
                  data-testid="demo-submit-button"
                  className="btn-kinetic w-full py-3.5 text-xs label-tiny tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {demoSubmitting ? "SENDING…" : "BOOK A DEMO"}
                  <PaperPlaneTilt size={15} weight="bold" />
                </button>
                <p className="text-[11px] text-white/50 text-center">
                  No spam. We'll only use your details to arrange the demo.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 px-6 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-6">
            ONE PLATFORM FOR<br /><span className="text-heat">ATHLETES, COACHES & ACADEMIES.</span>
          </h2>
          <p className="text-white/80 mb-10 max-w-xl mx-auto">
            Assess, program, monitor readiness and track progress — with everyone working from the same data.
          </p>
          <Link to="/register" data-testid="footer-cta-button">
            <button className="btn-kinetic px-10 py-4 text-sm label-tiny tracking-widest inline-flex items-center gap-2">
              GET STARTED FREE <ArrowRight size={16} weight="bold" />
            </button>
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-24 px-6 border-t border-white/10">
        <div className="max-w-3xl mx-auto">
          <div className="mb-12 text-center">
            <div className="label-tiny text-heat mb-4">/ DEBRIEF</div>
            <h2 className="font-heading text-4xl sm:text-5xl mb-4">
              QUESTIONS, <span className="text-heat">ANSWERED.</span>
            </h2>
          </div>
          <div className="space-y-px bg-white/10">
            {[
              {
                q: "Is Edge Training free?",
                a: "Yes — the free tier includes workout tracking, nutrition, recovery, goals, and 3 AI programs per month. Upgrade to Pro for unlimited AI, advanced analytics, and AI form check.",
              },
              {
                q: "Who is Edge Training for?",
                a: "Athletes, general fitness clients, strength & conditioning coaches, fitness trainers, sports coaches, academies and parents/guardians — each with a workspace built for their role.",
              },
              {
                q: "Can I cancel anytime?",
                a: "Yes. Pro extends your access by 30 or 365 days from purchase. No auto-renewal, no surprises. Just train.",
              },
              {
                q: "Does it work on my phone?",
                a: "Edge Training is fully responsive and installable as a PWA. Use it on any device with a browser. Native iOS and Android apps are on the way.",
              },
              {
                q: "Will my data stay private?",
                a: "Your workouts, photos, and readiness data are yours. We never sell or share them.",
              },
            ].map((f, i) => (
              <details key={i} className="bg-surface-base group" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer list-none flex items-center justify-between p-5 hover:bg-surface-elevated transition-colors">
                  <span className="font-heading text-base">{f.q.toUpperCase()}</span>
                  <span className="font-heading text-2xl text-heat group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="px-5 pb-5 text-sm text-white/80 border-t border-white/10 pt-4">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/icon-192.png" alt="Edge Training" className="h-8 w-8 rounded-md" />
            <span className="font-heading text-base tracking-tight leading-none text-white">EDGE TRAINING</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="label-tiny text-white/70 hover:text-heat transition-colors" data-testid="footer-privacy-link">PRIVACY</Link>
            <Link to="/terms" className="label-tiny text-white/70 hover:text-heat transition-colors" data-testid="footer-terms-link">TERMS</Link>
            <div className="label-tiny text-white/70">© 2026 — ASSESS. PROGRAM. PROGRESS.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
